import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import TmsSyncReportModal from "./TmsSyncReportModal";
import {
  Plus, Inbox, ListChecks, Settings, LogOut, ShieldCheck,
  Mail, Search, HelpCircle, Upload, BarChart3, FileText,
  Users, CreditCard, Building2, Tags, Plug, Info, RefreshCw, Banknote,
} from "lucide-react";

const ROLE_LABEL = { admin: "Admin", accounting: "Accounting", worker: "Worker" };
const TMS_STATUS_TTL = 60_000;

/* -------------------------------- helpers -------------------------------- */
function initialsFrom(user) {
  if (!user) return "?";
  const name = (user.name || "").trim();
  if (name) {
    const parts = name.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  const local = (user.email || "").split("@")[0] || "";
  return local.slice(0, 2).toUpperCase() || "?";
}

function Avatar({ user, size = 36, testId }) {
  return (
    <div
      data-testid={testId}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
      className="flex items-center justify-center rounded-full bg-[#0F1E3D] font-semibold text-white">
      {initialsFrom(user)}
    </div>
  );
}

function CountPill({ value, testId }) {
  if (!value) return null;
  return (
    <span data-testid={testId}
      className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold leading-none text-white">
      {value}
    </span>
  );
}

function StatusDot({ status, className = "h-2 w-2" }) {
  let color = "bg-slate-300";
  if (status) {
    if (status.mode === "mock") color = "bg-amber-500";
    else if (status.mode === "alvys" && status.token_ok) color = "bg-emerald-500";
    else if (status.mode === "alvys" && !status.token_ok) color = "bg-rose-500";
  }
  return <span className={`inline-block rounded-full ${color} ${className}`} />;
}

function PlaidStatusDot({ status, className = "h-2 w-2" }) {
  let color = "bg-slate-300";
  if (status) {
    if (status.mode === "mock") color = "bg-amber-500";
    else if (status.mode === "plaid" && status.has_credentials && status.encryption_key_set) color = "bg-emerald-500";
    else if (status.mode === "plaid") color = "bg-rose-500";
  }
  return <span className={`inline-block rounded-full ${color} ${className}`} />;
}

/* --------------------------- Integrations modal -------------------------- */
function IntegrationsStatusModal({
  open, onOpenChange, tmsStatus, plaidStatus,
  tmsLoading, plaidLoading,
  onRefreshTms, onRefreshPlaid, onManagePlaid, onOpenSyncReport,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="integrations-modal" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Integrations</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* ---- Alvys TMS ---- */}
          <div className="rounded-md border border-slate-200 p-4" data-testid="integrations-tms-card">
            <div className="mb-3 flex items-center gap-2">
              <StatusDot status={tmsStatus} className="h-2.5 w-2.5" />
              <h3 className="font-medium">Alvys TMS</h3>
              <Button variant="ghost" size="sm" onClick={onRefreshTms} disabled={tmsLoading}
                data-testid="integrations-refresh-btn" className="ml-auto">
                <RefreshCw className={`mr-1 h-3 w-3 ${tmsLoading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Mode</dt>
                <dd className="font-mono text-slate-800" data-testid="integrations-mode">{tmsStatus?.mode || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Token OK</dt>
                <dd className={tmsStatus?.token_ok ? "text-emerald-700" : "text-rose-700"} data-testid="integrations-tokenok">
                  {tmsStatus?.token_ok ? "✓" : "✗"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Forced</dt>
                <dd className="text-slate-800">{tmsStatus?.forced ? "yes" : "no"}</dd></div>
              {tmsStatus?.last_error && (
                <div>
                  <dt className="text-slate-500">Last error</dt>
                  <dd>
                    <pre className="mt-1 max-h-32 overflow-auto rounded bg-rose-50 p-2 font-mono text-xs text-rose-800">{tmsStatus.last_error}</pre>
                  </dd>
                </div>
              )}
            </dl>
            <div className="mt-3 flex justify-end">
              <Button size="sm" variant="outline" onClick={onOpenSyncReport}
                data-testid="integrations-tms-sync-report-btn">
                View sync report
              </Button>
            </div>
          </div>

          {/* ---- Plaid ---- */}
          <div className="rounded-md border border-slate-200 p-4" data-testid="integrations-plaid-card">
            <div className="mb-3 flex items-center gap-2">
              <PlaidStatusDot status={plaidStatus} className="h-2.5 w-2.5" />
              <Banknote className="h-4 w-4 text-slate-600" />
              <h3 className="font-medium">Plaid</h3>
              <Button variant="ghost" size="sm" onClick={onRefreshPlaid} disabled={plaidLoading}
                data-testid="integrations-plaid-refresh-btn" className="ml-auto">
                <RefreshCw className={`mr-1 h-3 w-3 ${plaidLoading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Mode</dt>
                <dd className="font-mono text-slate-800" data-testid="integrations-plaid-mode">{plaidStatus?.mode || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Environment</dt>
                <dd className="font-mono text-slate-800" data-testid="integrations-plaid-env">{plaidStatus?.env || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Credentials</dt>
                <dd className={plaidStatus?.has_credentials ? "text-emerald-700" : "text-amber-700"} data-testid="integrations-plaid-creds">
                  {plaidStatus?.has_credentials ? "configured" : "mock"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Token encryption</dt>
                <dd className={plaidStatus?.encryption_key_set ? "text-emerald-700" : "text-rose-700"} data-testid="integrations-plaid-enc">
                  {plaidStatus?.encryption_key_set ? "configured" : "missing"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Linked items</dt>
                <dd className="font-mono text-slate-800" data-testid="integrations-plaid-items">{plaidStatus?.item_count ?? 0}</dd></div>
            </dl>
            <div className="mt-3 flex justify-end">
              <Button size="sm" onClick={onManagePlaid} data-testid="integrations-plaid-manage-btn"
                className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
                Manage Plaid
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------ About modal ------------------------------ */
function AboutModal({ open, onOpenChange }) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/version").then((r) => setInfo(r.data)).catch(() => setInfo(null));
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="about-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle>About ExpenseReconcile</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-3">
            <span className="inline-block h-3 w-3 rounded-sm bg-[#0F1E3D]" />
            <div>
              <p className="font-semibold">{info?.name || "ExpenseReconcile"}</p>
              <p className="text-xs text-slate-500">Company expense logging + statement reconciliation</p>
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-y-1.5 text-sm">
            <dt className="text-slate-500">Version</dt>
            <dd className="font-mono" data-testid="about-version">{info?.version || "—"}</dd>
            <dt className="text-slate-500">Environment</dt>
            <dd><Badge variant="secondary" className="bg-slate-100" data-testid="about-env">{info?.env || "—"}</Badge></dd>
          </dl>
          <p className="text-xs text-slate-400">© 2026</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------- Item helpers ----------------------------- */
function Item({ icon: Icon, label, badge, onSelect, testId, danger }) {
  return (
    <DropdownMenuItem onSelect={onSelect} data-testid={testId}
      className={`min-h-[44px] cursor-pointer gap-2 ${danger ? "text-rose-600 focus:text-rose-700" : ""}`}>
      {Icon && <Icon className="h-4 w-4" />}
      <span>{label}</span>
      <CountPill value={badge} testId={testId ? `${testId}-badge` : undefined} />
    </DropdownMenuItem>
  );
}

/* ============================== UserDropdown ============================== */
export default function UserDropdown() {
  const { user, logout, refreshCounters } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  const [syncReportOpen, setSyncReportOpen] = useState(false);
  const [tmsStatus, setTmsStatus] = useState(null);
  const [tmsLoading, setTmsLoading] = useState(false);
  const [plaidStatus, setPlaidStatus] = useState(null);
  const [plaidLoading, setPlaidLoading] = useState(false);
  const tmsFetchedAt = useRef(0);
  const plaidFetchedAt = useRef(0);

  const counters = user?.counters || {};
  const role = user?.role;
  const dept = useMemo(() => {
    if (!user?.default_department_id) return "No default";
    if (user?.default_department_name && user?.default_department_code) {
      return `${user.default_department_name} (${user.default_department_code})`;
    }
    return user.default_department_code || user.default_department_name || "Default";
  }, [user]);
  const company = useMemo(() => {
    if (!user?.default_company_id) return null;
    if (user?.default_company_name && user?.default_company_code) {
      return `${user.default_company_name} (${user.default_company_code})`;
    }
    return user.default_company_code || user.default_company_name || null;
  }, [user]);

  // Lazy + cached TMS status (admin + accounting; backend now allows both).
  const fetchTmsStatus = async (force = false) => {
    if (role !== "admin" && role !== "accounting") return;
    if (!force && Date.now() - tmsFetchedAt.current < TMS_STATUS_TTL) return;
    setTmsLoading(true);
    try {
      const { data } = await api.get("/integrations/tms/status");
      setTmsStatus(data);
      tmsFetchedAt.current = Date.now();
    } catch {
      setTmsStatus(null);
    } finally {
      setTmsLoading(false);
    }
  };

  // Lazy + cached Plaid status (admin + accounting).
  const fetchPlaidStatus = async (force = false) => {
    if (role !== "admin" && role !== "accounting") return;
    if (!force && Date.now() - plaidFetchedAt.current < TMS_STATUS_TTL) return;
    setPlaidLoading(true);
    try {
      const { data } = await api.get("/integrations/plaid/status");
      setPlaidStatus(data);
      plaidFetchedAt.current = Date.now();
    } catch {
      setPlaidStatus(null);
    } finally {
      setPlaidLoading(false);
    }
  };

  // Open → refresh counters + lazy status fetches
  useEffect(() => {
    if (!open) return;
    refreshCounters?.();
    fetchTmsStatus(false);
    fetchPlaidStatus(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!user) return null;

  const go = (to) => () => { setOpen(false); navigate(to); };
  const openModal = (setter) => () => { setOpen(false); setTimeout(() => setter(true), 80); };
  const onLogout = () => { setOpen(false); logout(); navigate("/login", { replace: true }); };

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-avatar-btn" aria-label="Account menu"
                  className="rounded-full ring-offset-2 ring-offset-white focus:outline-none focus:ring-2 focus:ring-[#0F1E3D]">
                  <Avatar user={user} size={36} testId="user-avatar" />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">Account menu</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <DropdownMenuContent align="end" sideOffset={8} collisionPadding={8}
          data-testid="user-dropdown-content"
          style={{ width: "min(calc(100vw - 1rem), 24rem)" }}
          className="p-0">
          {/* HEADER */}
          <div className="flex items-center gap-3 p-4" data-testid="dropdown-header">
            <Avatar user={user} size={48} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-slate-900" data-testid="dropdown-user-name">{user.name}</p>
              <p className="truncate text-xs text-slate-500" data-testid="dropdown-user-email">{user.email}</p>
              <div className="mt-1 flex items-center gap-1.5">
                <Badge variant="secondary" className="bg-[#0F1E3D]/5 text-[#0F1E3D] hover:bg-[#0F1E3D]/10" data-testid="dropdown-role-badge">
                  <ShieldCheck className="mr-1 h-3 w-3" />{ROLE_LABEL[role] || role}
                </Badge>
                <span className="truncate text-xs text-slate-500" data-testid="dropdown-default-dept">{dept}</span>
              </div>
              {company && (
                <p className="mt-0.5 truncate text-xs text-slate-500" data-testid="dropdown-default-company">
                  <Building2 className="mr-1 inline h-3 w-3 -translate-y-0.5" />{company}
                </p>
              )}
            </div>
          </div>
          <DropdownMenuSeparator />

          {/* WORKER */}
          {role === "worker" && (
            <div className="py-1">
              <Item icon={Plus} label="Log expense" onSelect={go("/expenses/new")} testId="dropdown-log-expense" />
              <Item icon={Inbox} label="My inbox" badge={counters.open_comment_requests} onSelect={go("/inbox")} testId="dropdown-inbox" />
              <Item icon={ListChecks} label="My expenses" onSelect={go("/expenses")} testId="dropdown-my-expenses" />
            </div>
          )}

          {/* ACCOUNTING (also rendered for admin below) */}
          {(role === "accounting" || role === "admin") && (
            <>
              <div className="py-1">
                <Item icon={Mail} label="Open chases" badge={counters.open_chases}
                  onSelect={go("/statements?match_status=chase")} testId="dropdown-open-chases" />
                <Item icon={Search} label="Unmatched lines" badge={counters.unmatched_lines}
                  onSelect={go("/statements?match_status=unmatched")} testId="dropdown-unmatched-lines" />
                <Item icon={HelpCircle} label="Ambiguous lines" badge={counters.ambiguous_lines}
                  onSelect={go("/statements?match_status=ambiguous")} testId="dropdown-ambiguous-lines" />
                <Item icon={Upload} label="Upload statement" onSelect={go("/statements?upload=1")} testId="dropdown-upload-statement" />
              </div>
              <DropdownMenuSeparator />
              <div className="py-1">
                <Item icon={BarChart3} label="Reports" onSelect={go("/reports")} testId="dropdown-reports" />
                <Item icon={FileText} label="All expenses" onSelect={go("/expenses")} testId="dropdown-all-expenses" />
              </div>
              {/* Integrations + Plaid quick-link — accounting team manages
                  banks too (Phase 12e). Admin-only items stay below. */}
              <DropdownMenuSeparator />
              <div className="py-1">
                <DropdownMenuItem onSelect={openModal(setIntegrationsOpen)} data-testid="dropdown-integrations"
                  className="min-h-[44px] cursor-pointer gap-2">
                  <Plug className="h-4 w-4" />
                  <span>Integrations</span>
                  <span className="ml-auto inline-flex items-center gap-1">
                    <StatusDot status={tmsStatus} className="h-2 w-2" />
                    <PlaidStatusDot status={plaidStatus} className="h-2 w-2" />
                  </span>
                </DropdownMenuItem>
                <Item icon={Banknote} label="Plaid · Connect bank" onSelect={go("/admin/integrations/plaid")} testId="dropdown-plaid-admin" />
              </div>
            </>
          )}

          {/* ADMIN-only blocks */}
          {role === "admin" && (
            <>
              <DropdownMenuSeparator />
              <div className="py-1">
                <Item icon={Settings} label="Admin" onSelect={go("/admin")} testId="dropdown-admin-landing" />
                <Item icon={Users} label="Users" onSelect={go("/admin/users")} testId="dropdown-admin-users" />
                <Item icon={CreditCard} label="Cards" onSelect={go("/admin/cards")} testId="dropdown-admin-cards" />
                <Item icon={Building2} label="Departments" onSelect={go("/admin/departments")} testId="dropdown-admin-departments" />
                <Item icon={Building2} label="Companies" onSelect={go("/admin/companies")} testId="dropdown-admin-companies" />
                <Item icon={Tags} label="Categories" onSelect={go("/admin/categories")} testId="dropdown-admin-categories" />
              </div>
              <DropdownMenuSeparator />
              <div className="py-1">
                <Item icon={Info} label="About" onSelect={openModal(setAboutOpen)} testId="dropdown-about" />
              </div>
            </>
          )}

          {/* FOOTER */}
          <DropdownMenuSeparator />
          <div className="py-1">
            <Item icon={Settings} label="Account settings" onSelect={go("/account")} testId="dropdown-account-settings" />
            {role !== "admin" && (
              <Item icon={Info} label="About" onSelect={openModal(setAboutOpen)} testId="dropdown-about" />
            )}
            <Item icon={LogOut} label="Sign out" onSelect={onLogout} testId="dropdown-logout" danger />
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <IntegrationsStatusModal
        open={integrationsOpen} onOpenChange={setIntegrationsOpen}
        tmsStatus={tmsStatus} plaidStatus={plaidStatus}
        tmsLoading={tmsLoading} plaidLoading={plaidLoading}
        onRefreshTms={() => fetchTmsStatus(true)}
        onRefreshPlaid={() => fetchPlaidStatus(true)}
        onManagePlaid={() => { setIntegrationsOpen(false); navigate("/admin/integrations/plaid"); }}
        onOpenSyncReport={() => { setIntegrationsOpen(false); setTimeout(() => setSyncReportOpen(true), 80); }}
      />
      <TmsSyncReportModal open={syncReportOpen} onOpenChange={setSyncReportOpen} />
      <AboutModal open={aboutOpen} onOpenChange={setAboutOpen} />
    </>
  );
}
